import React, { useEffect } from 'react';
import { Box, Typography, Stack, IconButton, Collapse } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { useCoordinateContext } from '~/hooks/coordinate/useCoordinateContext';

const AUTO_DISMISS_MS = 10_000;

const CoordinateNotificationCenter: React.FC = () => {
  const { notifications, dismissNotification } = useCoordinateContext();
  const navigate = useNavigate();

  // Auto-dismiss each notification after 10 seconds
  useEffect(() => {
    if (notifications.length === 0) return;
    const latest = notifications[notifications.length - 1];
    const timer = setTimeout(() => dismissNotification(latest.id), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [notifications, dismissNotification]);

  if (notifications.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxWidth: 360,
      }}
    >
      {notifications.slice(-3).map(n => (
        <Collapse key={n.id} in appear>
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid',
              borderColor: 'rgba(123, 31, 162, 0.35)',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              p: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
            }}
          >
            <Box
              sx={{
                width: 32, height: 32, borderRadius: '50%',
                bgcolor: '#f0eef8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16, color: '#635f99' }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} color="#635f99" sx={{ display: 'block', mb: 0.25 }}>
                Coordinate AI
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.4 }}>
                {n.message}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                <Box
                  component="span"
                  onClick={() => {
                    dismissNotification(n.id);
                    navigate(`/coordinate/patient/${n.patientId}`);
                  }}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.4,
                    fontSize: '0.72rem', fontWeight: 700, color: '#635f99',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 12 }} />
                  View {n.patientName}
                </Box>
              </Stack>
            </Box>

            <IconButton
              size="small"
              onClick={() => dismissNotification(n.id)}
              sx={{ mt: -0.5, mr: -0.5, color: 'text.disabled' }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Collapse>
      ))}
    </Box>
  );
};

export default CoordinateNotificationCenter;
